import React from 'react';
import { C, DISP, BODY, MONO } from './shared';

/* Signature visual: the ranked Daily Growth Plan, built from real product
   capabilities (daily plan, content engine, email, carousel, competitor intel). */

const ACTIONS = [
  {
    title: 'Announce the restock to your email list',
    why: 'Your audience engaged most with product updates this month',
    priority: 'HIGH', effort: '~15 min', assets: 'Email + IG post', active: true, done: false,
  },
  { title: 'Post the customer story carousel', priority: 'MEDIUM', effort: '~10 min', assets: 'Carousel', done: true },
  { title: "Review this week's competitor moves", priority: 'LOW', effort: '~5 min', assets: 'Intel report', done: false },
];

const prColor = (p) => (p === 'HIGH' ? C.lime : p === 'MEDIUM' ? '#f6c453' : C.muted);

export const DailyGrowthPlan = () => (
  <div data-testid="daily-growth-plan-visual" aria-label="Example of a ScalitOS Daily Growth Plan" style={{
    position: 'relative', width: '100%', maxWidth: 460, marginLeft: 'auto',
    background: 'rgba(10,14,8,0.88)', border: `1px solid ${C.border}`, borderRadius: 20,
    boxShadow: '0 30px 80px -20px rgba(0,0,0,0.65), 0 0 0 1px rgba(200,241,53,0.05) inset',
    overflow: 'hidden',
  }}>
    <div style={{ position: 'absolute', top: 0, left: 24, right: 24, height: 1, background: 'linear-gradient(90deg, transparent, rgba(200,241,53,0.4), transparent)' }} />

    <div style={{ padding: '20px 22px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <p style={{ fontFamily: DISP, fontWeight: 700, fontSize: 16, color: C.text }}>Today&rsquo;s Growth Plan</p>
        <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', color: C.muted, marginTop: 3, textTransform: 'uppercase' }}>Ranked by impact</p>
      </div>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, background: 'rgba(200,241,53,0.08)', border: `1px solid ${C.borderLime}` }}>
        <span className="scl-pulse-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: C.lime }} />
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.14em', color: C.lime }}>EXAMPLE GROWTH PLAN</span>
      </span>
    </div>

    <div style={{ padding: '14px 16px' }}>
      {ACTIONS.map((a, i) => (
        <div key={i} style={{
          padding: '14px 14px',
          marginBottom: 10,
          borderRadius: 12,
          border: a.active ? `1px solid ${C.borderLime}` : `1px solid ${C.border}`,
          background: a.active ? 'rgba(200,241,53,0.05)' : 'rgba(255,255,255,0.015)',
          opacity: a.done ? 0.55 : 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span aria-hidden="true" style={{
              width: 18, height: 18, borderRadius: 6, flexShrink: 0, marginTop: 2,
              border: a.done ? 'none' : `1.5px solid ${a.active ? C.lime : C.border}`,
              background: a.done ? C.lime : 'transparent',
              display: 'grid', placeItems: 'center',
            }}>
              {a.done && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l2.5 2.5L9 1" stroke={C.bg} strokeWidth="2" strokeLinecap="round" /></svg>}
            </span>
            <div style={{ flex: 1 }}>
              <p style={{ fontFamily: BODY, fontWeight: 600, fontSize: 14, color: C.text, textDecoration: a.done ? 'line-through' : 'none', lineHeight: 1.4 }}>{a.title}</p>
              {a.why && (
                <p style={{ marginTop: 6, fontFamily: BODY, fontSize: 12.5, color: C.body, lineHeight: 1.55 }}>
                  <span style={{ color: C.lime, fontWeight: 600 }}>Why: </span>{a.why}
                </p>
              )}
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', padding: '3px 8px', borderRadius: 999, border: `1px solid ${prColor(a.priority)}44`, color: prColor(a.priority) }}>{a.priority}</span>
                <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', padding: '3px 8px', borderRadius: 999, border: `1px solid ${C.border}`, color: C.muted }}>{a.effort}</span>
                <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', padding: '3px 8px', borderRadius: 999, border: `1px solid ${C.border}`, color: C.muted }}>{a.assets}</span>
              </div>
              {a.active && (
                <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: C.lime, color: C.bg, fontFamily: BODY, fontWeight: 700, fontSize: 12.5 }}>
                  Create the assets →
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>

    <div style={{ padding: '14px 22px 18px', borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontFamily: BODY, fontSize: 12.5, color: C.body }}>1 of 3 actions completed today</span>
        <span style={{ fontFamily: MONO, fontSize: 11, color: C.lime }}>33%</span>
      </div>
      <div role="progressbar" aria-valuenow={33} aria-valuemin={0} aria-valuemax={100} style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <div style={{ width: '33%', height: '100%', borderRadius: 999, background: C.lime }} />
      </div>
      <p style={{ marginTop: 12, fontFamily: BODY, fontSize: 12.5, color: C.muted }}>
        Next: schedule Thursday&rsquo;s launch teaser
      </p>
    </div>
  </div>
);
